import { Link, useNavigate } from "react-router-dom";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { PasswordInput } from "./PasswordInput";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { AlertCircle, Ban } from "lucide-react";
import { VerificationRequired } from "../VerificationRequired";

export const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState("");
  const [banInfo, setBanInfo] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setBanInfo(null);

    // Basic validation
    if (!formData.email || !formData.password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    try {
      const result = await login(formData.email, formData.password, formData.remember);
      
      console.log("Login result:", result);

      if (result.success) {
        console.log("Logged-in user:", result);
        // Redirect to home page
        navigate("/");
      } else {
        // Check if user is banned - check multiple possible response structures
        const isBanned = result.banned || result.banInfo || 
                        (result.message && (result.message.includes("suspended") || result.message.includes("banned")));
        
        if (isBanned) {
          console.log("User is banned:", result);
          setBanInfo(result.banInfo || { type: result.banType || 'unknown' });
          setError(result.message || "Your account has been suspended.");
        } else {
          // Check for email verification requirement
          const needsVerification = result.requiresVerification || 
                                   result.emailNotVerified || 
                                   result.error?.includes("verify") ||
                                   result.error?.includes("verification") ||
                                   result.message?.includes("verify") ||
                                   result.message?.includes("verification");

          if (needsVerification) {
            console.log("Email verification required");
            setUnverifiedEmail(result.email || formData.email);
            setShowVerificationModal(true);
          } else {
            // Handle other types of errors
            setError(result.error || result.message || "Login failed. Please check your credentials.");
          }
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      
      // Check if the error response indicates user is banned
      const errorData = err.data || err.response?.data || {};
      const statusCode = err.status || err.response?.status;
      
      if (statusCode === 403 || errorData.banned || errorData.banInfo || errorData.forceLogout) {
        console.log("User is banned (from catch):", errorData);
        setBanInfo(errorData.banInfo || { type: 'unknown' });
        setError(errorData.message || "Your account has been suspended.");
      } else {
        // Check if the error indicates email verification is needed
        const errorMessage = errorData.message || err.message || err.toString();
        const needsVerification = errorMessage.includes("verify") || 
                                 errorMessage.includes("verification") ||
                                 errorMessage.includes("email not verified");

        if (needsVerification) {
          console.log("Email verification required (from catch)");
          setUnverifiedEmail(formData.email);
          setShowVerificationModal(true);
        } else {
          setError(errorData.message || "An unexpected error occurred. Please try again.");
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterClick = () => {
    if (!isLoading) {
      navigate("/auth?mode=register");
    }
  };

  // Render ban-specific error message
  const renderBanError = () => {
    if (!banInfo) return null;

    const isTempBan = banInfo.type === 'temporary';
    const banEndDate = banInfo.expiresAt ? new Date(banInfo.expiresAt).toLocaleString() : null;

    return (
      <div className="p-4 border border-red-300 rounded-lg bg-red-50">
        <div className="flex items-start gap-3">
          <Ban className="text-red-600 mt-0.5" size={20} />
          <div className="flex-1">
            <h4 className="font-semibold text-red-800 mb-2">
              Account {isTempBan ? 'Temporarily' : 'Permanently'} Suspended
            </h4>
            <p className="text-red-700 text-sm mb-2">{error}</p>
            
            {banInfo.reason && (
              <p className="text-red-600 text-sm mb-2">
                <span className="font-medium">Reason:</span> {banInfo.reason}
              </p>
            )}
            
            {isTempBan && banEndDate && (
              <p className="text-red-600 text-sm">
                <span className="font-medium">Suspension ends:</span> {banEndDate}
              </p>
            )}
            
            {!isTempBan && (
              <p className="text-red-600 text-sm">
                This suspension is permanent. Please contact support if you believe this is an error.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-white p-6 rounded-xl border border-border/60 shadow-sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              className="focus-visible:ring-igbanwo-mint"
              placeholder="Enter your email"
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">Password</Label>
              <Link
                to="/forgot-password"
                className="text-sm text-igbanwo-mint hover:text-igbanwo-mint/80"
              >
                Forgot Password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          {/* Ban-specific error display */}
          {banInfo && renderBanError()}

          {/* Regular error display (only if not banned) */}
          {error && !banInfo && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <Checkbox
              id="remember"
              name="remember"
              checked={formData.remember}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, remember: checked }))
              }
              className="data-[state=checked]:bg-igbanwo-mint data-[state=checked]:border-igbanwo-mint"
              disabled={isLoading}
            />
            <Label htmlFor="remember" className="text-sm font-normal">
              Remember me for 30 days
            </Label>
          </div>
          
          <Button
            type="submit"
            disabled={isLoading || banInfo}
            className="w-full bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white disabled:bg-gray-400"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing In...
              </div>
            ) : banInfo ? (
              "Account Suspended"
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        {!banInfo && (
          <div className="mt-6 text-center text-sm">
            <p className="text-muted-foreground">
              Don't have an account?{" "}
              <button
                type="button"
                className="text-igbanwo-mint hover:text-igbanwo-mint/80 font-medium"
                disabled={isLoading}
                onClick={handleRegisterClick}
              >
                Register
              </button>
            </p>
          </div>
        )}
      </div>

      {/* Verification Required Modal */}
      {showVerificationModal && (
        <VerificationRequired 
          email={unverifiedEmail}
          onClose={() => setShowVerificationModal(false)}
        />
      )}
    </>
  );
};